import * as z from 'zod/v4';

import { type Bet, type BetEvent, type Board, type Coupon, NO_VALUE_SET, type OutcomeGroup } from '../coupon/entry.js';
import { drawNumberSchema } from '../draw/entry.js';
import { dateTimeSchema } from '../frameworks/entry.js';
import { currencySchema } from '../lib/entry.js';
import { couponProductSchema } from '../product/entry.js';
import type { SystemType } from '../system/entry.js';
import { OUTCOME_MAP_1X2 } from './parsers/utils.js';

export type ExternalWagerSystemType = Extract<SystemType, 'MSYS' | 'SROW'>;
export type ExternalWagerSelectedSystemType = ExternalWagerSystemType;
export type ExternalWagerCoupon = Omit<Coupon, 'selectedSystem'> & {
  selectedSystem: ExternalWagerSystemType;
};

const externalWagerSystemEnum = z.enum(['single', 'math']);
const SYSTEM_TRANSLATE_TABLE = {
  math: 'MSYS',
  single: 'SROW',
} as const satisfies Record<z.infer<typeof externalWagerSystemEnum>, ExternalWagerSystemType>;

const externalWagerSystemSchema = externalWagerSystemEnum.transform(
  (externalWagerSystem) => SYSTEM_TRANSLATE_TABLE[externalWagerSystem] satisfies ExternalWagerSystemType
);

function parseExternalWager1X2Board(board: string): Bet {
  const valuePairs = board.toUpperCase().split(',');

  const bet: Bet = [];
  for (const values of valuePairs) {
    const outcomeGroup: OutcomeGroup = [];
    for (const value of values.split('')) {
      const outcome = OUTCOME_MAP_1X2.get(value);

      if (outcome === undefined) {
        continue;
      }

      outcomeGroup.push(outcome);
    }
    const betEvent: BetEvent = [outcomeGroup];
    bet.push(betEvent);
  }

  return bet;
}

const externalWager1x2ItemsSchema = z.string().transform(parseExternalWager1X2Board);

export const externalWagerIdSchema = z.uuid().brand<'ExternalWagerId'>();
export type ExternalWagerId = z.infer<typeof externalWagerIdSchema>;

export const postExternalWagerSchema = z.object({
  wagerId: externalWagerIdSchema,
});

export const getExternalWagerSchema = z
  .object({
    productId: couponProductSchema,
    drawNumber: drawNumberSchema,
    system: externalWagerSystemSchema,
    items: z.array(externalWager1x2ItemsSchema),
    betRowAmount: z.number(),
    paid: z.boolean(),
    timestamp: dateTimeSchema,
    amount: currencySchema,
    totalNumberOfItems: z.number(),
    client: z.string(),
  })
  .transform((externalWager) => {
    const boards: Board[] = [];

    for (const outcomes of externalWager.items) {
      const board = {
        mOutcomes: Array.from<false>({ length: outcomes.length }).fill(false),
        uOutcomes: Array.from<number | null>({ length: outcomes.length }).fill(NO_VALUE_SET),
        // Skip calculate distribution for external systems
        outcomeDistribution: [],
        type: externalWager.system === 'MSYS' ? 'BOARD_MULTIPLE' : 'BOARD_SINGLE',
        outcomes,
        userOutcomes: [],
        quickPickSize: undefined,
        quickPickVariant: undefined,
        reduction: undefined,
      } satisfies Board;

      boards.push(board);
    }

    const coupon = {
      betAmount: externalWager.betRowAmount,
      boards: boards,
      drawNumber: externalWager.drawNumber,
      product: externalWager.productId,
      selectedSystem: externalWager.system,
      sportkryss: 0,
      sportkryssNumber: '0000000',
      week: 1,
      couponType: 'external_system',
    } satisfies ExternalWagerCoupon;

    return {
      coupon,
      amount: externalWager.amount,
      totalNumberOfItems: externalWager.totalNumberOfItems,
      timestamp: dateTimeSchema.parse(externalWager.timestamp),
      client: externalWager.client,
    };
  });

// Put response doesn't return any intressering
export const putExternalWagerSchema = z.unknown().transform(() => undefined);
